import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Pronoun } from '@plush-book/shared';

export interface DraftPlushie {
  id?: string;
  name: string;
  type: string;
  pronoun: Pronoun;
  trait: string;
  ipChecked: boolean;
  photoCount: number;
}

export interface DraftState {
  bookId: string | null;
  publicId: string | null;
  storyTemplateId: string | null;
  childName: string;
  dedication: string;
  fromWhom: string;
  occasion: string;
  plushies: DraftPlushie[];
  upsellSecondCopy: boolean;
  setStory: (id: string) => void;
  setBook: (id: string, publicId: string) => void;
  setChildField: (key: 'childName' | 'dedication' | 'fromWhom' | 'occasion', v: string) => void;
  addPlushie: () => void;
  removePlushie: (i: number) => void;
  updatePlushie: (i: number, patch: Partial<DraftPlushie>) => void;
  setSecondCopy: (v: boolean) => void;
  reset: () => void;
}

const emptyPlushie: DraftPlushie = {
  name: '',
  type: '',
  pronoun: 'er',
  trait: '',
  ipChecked: false,
  photoCount: 0,
};

export const useGeneratorStore = create<DraftState>()(
  persist(
    (set) => ({
      bookId: null,
      publicId: null,
      storyTemplateId: null,
      childName: '',
      dedication: '',
      fromWhom: '',
      occasion: '',
      plushies: [{ ...emptyPlushie, name: 'Bruno', type: 'Bär', trait: 'tapfer' }],
      upsellSecondCopy: false,
      setStory: (id) => set({ storyTemplateId: id }),
      setBook: (id, publicId) => set({ bookId: id, publicId }),
      setChildField: (key, v) => set({ [key]: v } as Partial<DraftState>),
      addPlushie: () =>
        set((s) =>
          s.plushies.length >= 3 ? s : { plushies: [...s.plushies, { ...emptyPlushie }] },
        ),
      removePlushie: (i) =>
        set((s) => ({ plushies: s.plushies.filter((_, idx) => idx !== i) })),
      updatePlushie: (i, patch) =>
        set((s) => ({
          plushies: s.plushies.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
        })),
      setSecondCopy: (v) => set({ upsellSecondCopy: v }),
      reset: () =>
        set({
          bookId: null,
          publicId: null,
          storyTemplateId: null,
          childName: '',
          dedication: '',
          fromWhom: '',
          occasion: '',
          plushies: [{ ...emptyPlushie, name: '', type: '' }],
          upsellSecondCopy: false,
        }),
    }),
    { name: 'plush-book-generator-draft' },
  ),
);
